<script setup lang="ts">
import type { IEmits, IUiSearchbarOption, IUiSearchbarProps } from './UiSearchbar.types'

const props = withDefaults(defineProps<IUiSearchbarProps>(), {
  disabled: false,
})

const modelValue = defineModel<string>({ default: '' })
const emit = defineEmits<IEmits>()

const { t } = useI18n()

const isOpen = ref(false)
const optionRefs = ref<HTMLLIElement[]>([])
const activeIndex = ref(-1)

const inputId = useId()

const filtered = computed(() => {
  const query = modelValue.value.toLowerCase().trim()

  return props.options.filter(o => o.label.toLowerCase().includes(query))
})

const emptyLabel = computed(() => props.emptyText ?? t('common.searchbar.empty'))

const open = () => {
  isOpen.value = true
}

const onClose = () => {
  isOpen.value = false
  activeIndex.value = -1
}

const onSelect = (option: IUiSearchbarOption) => {
  modelValue.value = option.label

  emit('select', option)
  onClose()
}

const onKeydown = (e: KeyboardEvent) => {
  const { key } = e

  if (key === 'Escape' && isOpen.value) return onClose()
  if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(key)) return

  e.preventDefault()

  if (key === 'Enter' && (!isOpen.value || activeIndex.value < 0)) {
    emit('submit', modelValue.value)
    return onClose()
  }

  if (!isOpen.value) return open()

  const last = filtered.value.length - 1
  const index = activeIndex.value

  if (key === 'ArrowDown' && index < last) activeIndex.value = index + 1
  if (key === 'ArrowUp' && index > 0) activeIndex.value = index - 1
  if (key === 'Enter' && index >= 0) return onSelect(filtered.value[index]!)

  optionRefs.value[activeIndex.value]?.scrollIntoView({ block: 'nearest' })
}

const onClear = () => {
  modelValue.value = ''

  emit('submit', '')
  onClose()
}

const highlightParts = (text: string) => {
  const query = modelValue.value.toLowerCase().trim()
  const matchIndex = query ? text.toLowerCase().indexOf(query) : -1

  if (matchIndex < 0) return [{ text }]

  return [
    { text: text.slice(0, matchIndex) },
    { text: text.slice(matchIndex, matchIndex + query.length), match: true },
    { text: text.slice(matchIndex + query.length) },
  ]
}

watch(modelValue, () => { activeIndex.value = -1 })
</script>

<template>
  <div :class="['ui-searchbar', { 'ui-searchbar--disabled': disabled }]">
    <label
      v-if="label"
      :for="inputId"
      class="ui-searchbar__label"
    >
      {{ label }}
    </label>

    <div class="ui-searchbar__input-wrapper">
      <input
        :id="inputId"
        v-model="modelValue"
        class="ui-searchbar__input"
        type="text"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="isOpen"
        :aria-activedescendant="activeIndex >= 0 ? String(filtered[activeIndex]!.value) : undefined"
        :placeholder="placeholder"
        :disabled="disabled"
        @focus="open"
        @click="open"
        @input="open"
        @blur="onClose"
        @keydown="onKeydown"
      >

      <button
        v-if="modelValue"
        type="button"
        class="ui-searchbar__icon ui-searchbar__icon--clear"
        :disabled="disabled"
        :aria-label="$t('common.searchbar.clear')"
        @mousedown.prevent
        @click="onClear"
      />

      <span
        v-else
        class="ui-searchbar__icon"
        aria-hidden="true"
      />
    </div>

    <div
      v-if="isOpen && (filtered.length || emptyLabel)"
      class="ui-searchbar__list-wrapper"
    >
      <ul
        v-if="filtered.length"
        class="ui-searchbar__list"
        role="listbox"
        :aria-label="label"
      >
        <li
          v-for="(option, index) in filtered"
          :id="String(option.value)"
          :ref="el => optionRefs[index] = el as HTMLLIElement"
          :key="option.value"
          :class="[
            'ui-searchbar__option',
            { 'ui-searchbar__option--active': index === activeIndex }
          ]"
          role="option"
          :aria-selected="index === activeIndex"
          @mousedown.prevent
          @click="onSelect(option)"
          @mouseenter="activeIndex = index"
        >
          <component
            :is="match ? 'mark' : 'span'"
            v-for="({ text, match }, idx) in highlightParts(option.label)"
            :key="idx"
            :class="{ 'ui-searchbar__highlight': match }"
          >
            {{ text }}
          </component>
        </li>
      </ul>

      <p
        v-else
        class="ui-searchbar__empty"
      >
        {{ emptyLabel }}
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.ui-searchbar {
  $root: &;

  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  width: 100%;

  &__label {
    @include body-3-medium;
    color: $text-400;
  }

  &__input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  &__input {
    @include body-2;
    width: 100%;
    padding: 1.2rem 4.4rem 1.2rem 1.6rem;
    color: $text-400;
    background-color: $white;
    border: 0.1rem solid $text-300;
    border-radius: 1.6rem;
    transition: border-color 0.2s ease;

    &::placeholder {
      color: $text-200;
    }

    &:hover:not(:disabled) {
      border-color: $text-400;
    }

    &:focus:not(:disabled) {
      border-color: $purple-400;
      outline: none;
    }

    &:disabled {
      color: $text-200;
      cursor: not-allowed;
    }
  }

  &__icon {
    position: absolute;
    right: 1.6rem;
    width: 1.8rem;
    height: 1.8rem;
    background-image: url('/icons/components/UiSearchbar/search.svg');
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
    pointer-events: none;

    &--clear {
      background-color: transparent;
      background-image: url('/icons/icn_close.svg');
      border: none;
      cursor: pointer;
      pointer-events: auto;
    }
  }

  &__list-wrapper {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 10;
    width: 100%;
    margin-top: 0.4rem;
    overflow: hidden;
    background-color: $white;
    border-radius: 1.6rem;
    box-shadow: 0 0.4rem 1.6rem 0 rgba(0, 0, 0, 0.08);
  }

  &__list {
    width: 100%;
    max-height: 24rem;
    margin: 0;
    padding: 0.8rem;
    overflow-y: auto;
    list-style: none;

    @include thin-scrollbar;
  }

  &__option {
    @include body-2;
    padding: 1.2rem 1.6rem;
    color: $text-400;
    border-radius: 1.2rem;
    cursor: pointer;

    &--active,
    &:hover {
      background-color: $gray-100;
    }
  }

  &__empty {
    @include body-2;
    margin: 0;
    padding: 1.2rem 1.6rem;
    color: $text-200;
  }

  &__highlight {
    color: inherit;
    font-weight: 700;
    background-color: transparent;
  }

  &--disabled {
    #{$root}__label {
      color: $text-200;
    }
  }
}
</style>
